import type React from 'react';
import { useState, useEffect, useCallback, useRef } from 'react';
import styled, { useTheme } from 'styled-components';
import { useIntl } from 'react-intl';

import ArrowDownLeft from 'app/ui/components/atoms/icons/arrow-down-left';
import ChevronRight from 'app/assets/chevron-right-icon.svg';
import PresentationIcon from 'app/assets/presentation-icon.svg';
import useAuxiliaryWindow from 'app/hooks/use-auxiliary-window';
import ChallengeIcon from 'app/assets/challenge-logo.svg';
import PoweredByOneFiit from 'app/assets/powered-by-one-fiit.svg';
import { getPathFromUrl, isInIframe } from 'src/app/utils/in-app-comunication/utils';
import { sendMessage as sendWindowMessage } from 'src/app/utils/in-app-comunication/window-messages';
import { useCommunicationService } from 'src/app/services/communication';
import { type AppMessage, MessageType } from 'src/app/utils/in-app-comunication/types';
import { trackEvent } from 'src/app/utils/analytics/tracker';
import type { WorkoutType } from 'src/app/pages/workout/util';
import { useAppState, useDispatch } from 'state';
import timerActions from 'actions/timer';
import HyroxLogo from 'src/app/ui/components/atoms/icons/hyrox-logo';

type SlideshowProps = {
  slides: React.ReactNode[];
  isPresenterView: boolean;
  onEndWhiteboard?: () => void;
  workout: WorkoutType;
  isChallenge?: boolean;
};

const LogoWrapper = styled.div<{ width?: string }>`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: ${({ theme }) => theme.spacing(5)};
  svg {
    width: 6.5rem;
  }

  ${({ theme: { media }, width }) => `
    ${media.large(
      () => `
        svg {
          width: ${width || '7.625rem'};
        }
      `,
    )}
  `}
`;
const SlideshowWrapper = styled.div<{ $isVisible: boolean }>`
  position: fixed;
  width: 100%;
  height: 100%;
  bottom: ${({ $isVisible }) => ($isVisible ? '0' : '-100%')};
  left: 0;
  overflow: hidden;
  z-index: 101;
  display: flex;
  flex-direction: column;
  transition: bottom 0.4s ease-in-out;
`;

const SlideContent = styled.div`
  flex-grow: 1;
  width: 100%;
`;

const Slide = styled.div`
  width: 100%;
  height: 100%;
`;

const NavigationStripe = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  background-color: ${({ theme }) => theme.colors.veryGray};
  padding: ${({ theme }) => `${theme.spacing(3)} ${theme.spacing(4)}`};
  z-index: 102;
  height: 6.5rem;
`;

const ChallengeNavigationStripe = styled(NavigationStripe)`
  background-color: ${({ theme }) => theme.colors.focoltone};
  height: 104px;
`;

const ActionButton = styled.button`
  padding: ${({ theme }) => theme.spacing(3)} ${({ theme }) => theme.spacing(4)};
  border-radius: ${({ theme }) => theme.spacing(2)};
  cursor: pointer;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  min-width: 15vw;
  max-width: 35vw;
  height: 80%;
  font-size: ${({ theme }) => theme.typography.xlarge};
`;

const CloseButton = styled(ActionButton)`
  background-color: transparent;
  color: ${({ theme }) => theme.colors.white};
  border: ${({ theme }) => `2px solid ${theme.colors.white}`};
  gap: ${({ theme }) => theme.spacing(2)};
`;

const PresentButton = styled(ActionButton)`
  background-color: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.black};
  border: none;
  gap: ${({ theme }) => theme.spacing(3)};
`;

const ButtonsContainer = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(3)};
  height: 100%;
  align-items: center;
`;

const NavigationButton = styled.button`
  width: 15vw;
  height: 80%;
  border-radius: ${({ theme }) => theme.spacing(2)};
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  background-color: ${({ theme }) => theme.colors.focoltone};
  border: none;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Arrow = styled.span<{ $isLeft?: boolean }>`
  color: ${({ theme }) => theme.colors.black};
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: baseline;
  justify-content: center;

  ${({ $isLeft }) => ($isLeft ? 'transform: rotate(-180deg);' : '')};
`;

const ArrowIcon = styled.img`
  height: 50%;
`;

const ActionButtonIcon = styled.img`
  height: ${({ theme }) => theme.spacing(7)};
`;

const ActionButtonArrow = styled(ArrowDownLeft)`
  height: ${({ theme }) => theme.spacing(7)};
  width: ${({ theme }) => theme.spacing(7)};
`;

const ActionButtonLabel = styled.span`
  display: none;
  ${({ theme: { media } }) => `${media.medium(
    () => `
      display: unset;
    `,
  )}
  `};
`;

const Slideshow: React.FC<SlideshowProps> = ({ slides, isPresenterView, onEndWhiteboard, workout, isChallenge }) => {
  const dispatch = useDispatch();
  const comms = useCommunicationService();
  const theme = useTheme();
  const intl = useIntl();

  const { isAuxiliaryWindowConnected, setAuxiliaryWindowLocation } = useAuxiliaryWindow();

  const [isAuxWindowConnected, setIsAuxWindowConnected] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [localVisible, setLocalVisible] = useState(false);

  // Use ref to avoid recreating the handler
  const handleMessageReceivedRef = useRef<(message: AppMessage) => void>();

  useEffect(() => {
    const checkConnected = async () => {
      try {
        const connected = await isAuxiliaryWindowConnected();
        setIsAuxWindowConnected(connected);
      } catch (error) {
        console.error('Error checking auxiliary window connection:', error);
        setIsAuxWindowConnected(false);
      }
    };
    checkConnected();
  }, [isAuxiliaryWindowConnected]);

  const closeLabel = intl.formatMessage({ id: 'whiteboard.close' });
  const presentLabel = intl.formatMessage({ id: 'whiteboard.present' });

  const allRoundsCompleted = useAppState((state) => state.timer.allRoundsCompleted);

  const closeSlideshow = useCallback(() => {
    if (isAuxWindowConnected) {
      return setAuxiliaryWindowLocation({});
    }
    const completedWorkout = currentIndex === slides.length - 1 && allRoundsCompleted;
    comms.sendMessage({
      type: MessageType.SLIDESHOW_CLOSE,
      workoutProgress: completedWorkout ? 'completed' : 'aborted',
    });
  }, [isAuxWindowConnected, setAuxiliaryWindowLocation, comms, currentIndex, slides.length, allRoundsCompleted]);

  const handleEndWhiteboard = useCallback(() => {
    setLocalVisible(false);
    closeSlideshow();

    setTimeout(() => {
      if (onEndWhiteboard) {
        onEndWhiteboard();
      }
    }, 400);
  }, [closeSlideshow, onEndWhiteboard]);

  const handleMessageReceived = useCallback(
    (message: AppMessage) => {
      if (message.type) {
        switch (message.type) {
          case MessageType.SLIDESHOW_CHANGE:
            if (message.slideIndex !== undefined) {
              setCurrentIndex(message.slideIndex);
            }
            break;
          case MessageType.SLIDESHOW_CLOSE:
            handleEndWhiteboard();
            break;
          default:
            break;
        }
      }
    },
    [handleEndWhiteboard],
  );

  // Update ref when handler changes
  useEffect(() => {
    handleMessageReceivedRef.current = handleMessageReceived;
  }, [handleMessageReceived]);

  const changeSlide = useCallback(
    (newIndex: number) => {
      if (newIndex === currentIndex) {
        return;
      }
      setCurrentIndex(newIndex);
      // Reset rounds completion when changing slides
      dispatch(timerActions.resetTimerCompletions());
      const message = { type: MessageType.SLIDESHOW_CHANGE, slideIndex: newIndex };
      comms.sendMessage(message);
    },
    [currentIndex, comms, dispatch],
  );

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const initialIndex = Number(params.get('slideIndex'));
    if (!Number.isNaN(initialIndex) && initialIndex >= 0 && initialIndex < slides.length) {
      setCurrentIndex(initialIndex);
    }

    setLocalVisible(true);
    // Reset rounds completion when slideshow initializes
    dispatch(timerActions.resetTimerCompletions());
  }, [slides.length, dispatch]);

  useEffect(() => {
    const removeListener = comms.onMessage((message) => {
      if (handleMessageReceivedRef.current) {
        handleMessageReceivedRef.current(message);
      }
    });
    return () => removeListener();
  }, [comms]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') {
        changeSlide(Math.max(0, currentIndex - 1));
      } else if (event.key === 'ArrowRight') {
        changeSlide(Math.min(slides.length - 1, currentIndex + 1));
      } else if (event.key === 'Escape') {
        handleEndWhiteboard();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, slides.length, changeSlide, handleEndWhiteboard]);

  const handlePresentButtonClick = useCallback(() => {
    trackEvent('whiteboard_athlete_view_opened', {
      workout_id: workout.id,
      workout_name: workout.name,
    });
    const newUrl = new URL(window.location.href);
    newUrl.searchParams.set('isPresenterView', 'false');
    newUrl.searchParams.set('isSlideshowOn', 'true');
    newUrl.searchParams.set('slideIndex', currentIndex.toString());
    if (isChallenge) {
      newUrl.searchParams.set('isChallenge', 'true');
    }
    if (isAuxWindowConnected) {
      return setAuxiliaryWindowLocation({ url: newUrl.toString() });
    }
    if (isInIframe()) {
      sendWindowMessage({ type: MessageType.ATHLETE_VIEW_OPEN, url: getPathFromUrl(newUrl.toString()) });
    } else {
      window.open(newUrl.toString(), '_blank');
    }
  }, [currentIndex, isAuxWindowConnected, setAuxiliaryWindowLocation, workout.id, workout.name]);

  return (
    <SlideshowWrapper $isVisible={localVisible} data-testid="sideshow-wrapper">
      <SlideContent>
        <Slide key={`slide-${currentIndex}`}>{slides[currentIndex]}</Slide>
      </SlideContent>
      {isPresenterView && (
        <NavigationStripe data-testid="navigation-stripe">
          <ButtonsContainer>
            <CloseButton onClick={handleEndWhiteboard}>
              <ActionButtonArrow color={theme.colors.white} />
              <ActionButtonLabel>{closeLabel}</ActionButtonLabel>
            </CloseButton>
            <PresentButton onClick={handlePresentButtonClick}>
              <ActionButtonIcon src={PresentationIcon} alt="Present now" />
              <ActionButtonLabel>{presentLabel}</ActionButtonLabel>
            </PresentButton>
          </ButtonsContainer>
          <ButtonsContainer>
            <NavigationButton onClick={() => changeSlide(Math.max(0, currentIndex - 1))} disabled={currentIndex === 0}>
              <Arrow $isLeft={true}>
                <ArrowIcon src={ChevronRight} alt="Previous" />
              </Arrow>
            </NavigationButton>
            <NavigationButton
              onClick={() => changeSlide(Math.min(slides.length - 1, currentIndex + 1))}
              disabled={currentIndex === slides.length - 1}
            >
              <Arrow>
                <ArrowIcon src={ChevronRight} alt="Next" />
              </Arrow>
            </NavigationButton>
          </ButtonsContainer>
        </NavigationStripe>
      )}
      {isChallenge && !isPresenterView && (
        <ChallengeNavigationStripe>
          <ButtonsContainer>
            <img src={ChallengeIcon} alt="Challenge" />
          </ButtonsContainer>
          <ButtonsContainer>
            <LogoWrapper>
              <HyroxLogo color={theme.colors.black} />
            </LogoWrapper>
            <img src={PoweredByOneFiit} alt="Powered by One Fiit" />
          </ButtonsContainer>
        </ChallengeNavigationStripe>
      )}
    </SlideshowWrapper>
  );
};

export default Slideshow;
